/* eslint-disable @typescript-eslint/strict-boolean-expressions */
import { apiClient, ApiResponse } from '@api/apiClient';
import {
  Box,
  Checkbox,
  Divider,
  DatePicker,
  Grid,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@atgfr8/atg-ui-tailwind';
import {
  CheckCircleIcon,
  CloseCircleIcon,
  CloseIcon,
  InformationIcon,
  InformationOutlineIcon,
  PencilIcon,
} from '@atgfr8/icons';
import { COUNTRY_CODES, equipmentTypes } from '@common/commonLists';
import { Button, Combobox, SelectInput, ToastType } from '@components';
import { AtgLoader } from '@components/AtgLoader';
import { RadioGroup } from '@components/RadioGroup/RadioGroup';
import HintTooltip from '@components/Tooltip/HintTooltip';
import { FeatureFlags } from '@constants/featureFlags';
import { createForm } from '@felte/solid';
import { validator } from '@felte/validator-yup';
import { isFeatureFlagEnabled } from '@store/featureFlags/store';
import { closeModal } from '@store/modals';
import {
  deletePosting,
  editPostLoad,
  fetchEquipmentTypeCodes,
  IManagePostLoad,
  IPostLoad,
  orderStore,
  savePostLoad,
  updateLoadPropertyAtIndex,
  updateOrderField,
} from '@store/orders';
import { ILoadEditViewModel } from '@typeDefinitions/loadTypes';
import { ILocationSearch } from '@typeDefinitions/locationTypes';
import {
  availableServices,
  EquipmentTypeCodeBoard,
} from '@typeDefinitions/postingTypes';
import {
  getPostingModeFromLoadMode,
  getPostingModeFromPosting,
  getPostingModePayload,
  POSTING_MODE_OPTIONS,
} from '@store/orders/mapper';
import { PostingMode } from '@store/orders/types';
import { LastUsedTopStopModel } from '@typeDefinitions/stopTypes';
import { formatDataTestId } from '@utils/dataTestIdFormatter';
import { logError } from '@utils/errorLogging';
import { createError } from '@utils/errorUtils';
import {
  boardHasSubcode,
  buildEquipmentOverridePayload,
  hydrateEquipmentOverrides,
  hydrateEquipmentSubcodeOverrides,
} from '@utils/postingEquipmentOverrides';
import { formatAmount, handleToast, printLog } from '@utils/utils';
import { postLoad } from '@views/collapsableSidebar/applicationPosting/validation';
import debounce from 'lodash/debounce';
import { createEffect, createSignal, onCleanup, Show } from 'solid-js';
import { CHANGE_TRACKING_SOURCES } from '@changeTracking/utils/changeTrackingRegistry';

import cls from './classes';
import { PostingPricingDetails } from './PostingPricingDetails';

type PostLoadValues = {
  origin: string;
  originCity: string;
  originState: string;
  originCountry: string;
  destination: string;
  destinationCity: string;
  destinationState: string;
  destinationCountry: string;
  originZip: string;
  destinationZip: string;
  date: string;
  equipment: string;
  pickUp: string;
  dropOff?: string;
  drops: number;
  boardsPostedTo?: string[] | null;
  servicesToPostTo: string[];
  length: number;
  price: number | null;
  tags: string[];
  internalNote: string;
  comment?: string;
  weight: number;
  mode: PostingMode;
  mileage: number | null;
  id: null | number;
  loadId: number;
  postingClients: [];
};

type Props = {
  modalId: string;
  tabIndex: number;
  services: availableServices[];
  postingId: number | null;
  load: ILoadEditViewModel;
};

const getLocationString = (stop: LastUsedTopStopModel | undefined) => {
  const { city, state } = stop || {};
  return `${city}, ${state}`;
};

const normalizeCountryCode = (country: string | undefined): string => {
  return country === 'CAN' ? 'CA' : country || 'USA';
};

const reverseNormalizeCountryCode = (country: string): string => {
  return country === 'CA' ? 'CAN' : country;
};

// eslint-disable-next-line complexity
export const PostLoad = (props: Props) => {
  const [isLoading, setLoading] = createSignal(false);
  const [isDeleteLoading, setDeleteLoading] = createSignal(false);
  const [showPricingDetails, setShowPricingDetails] = createSignal(false);
  const [boardEffectiveCodes, setBoardEffectiveCodes] = createSignal<
    Record<string, string | undefined>
  >({});
  // User-entered equipment-code overrides, keyed by board name. Persisted
  // across API refetches so manual edits are never wiped by fresh codes.
  const [editedCodes, setEditedCodes] = createSignal<
    Record<string, string | undefined>
  >({});
  // Truckstop subcode overrides, keyed by board name (only Truckstop has one).
  const [editedSubcodes, setEditedSubcodes] = createSignal<
    Record<string, string | undefined>
  >({});
  const [editingBoard, setEditingBoard] = createSignal<string | null>(null);
  const [isEquipmentCodesLoading, setIsEquipmentCodesLoading] =
    createSignal(false);
  // Gates the entire equipment-code override feature (display, edit field,
  // subcode and override payload). Hidden/inactive unless the flag is enabled.
  const isEquipmentCodeOverrideEnabled = isFeatureFlagEnabled(
    FeatureFlags.posting_equipmentcode_override,
  );
  const isAutoPopulatePostingFromLoadEnabled = isFeatureFlagEnabled(
    FeatureFlags.autopopulatepostingfromload,
  );
  const isPostingModeOptionsEnabled = isFeatureFlagEnabled(
    FeatureFlags.ReleasePostingModeOptions,
  );
  const canPrepopulateSingleComment = isFeatureFlagEnabled(
    FeatureFlags.feature_tms_Prepopulate_LoadNumber_Single_Posting_Comment,
  );

  const Tags = [
    { label: 'Team', key: 'teamRequired', loadKey: 'teamRequired' },
    { label: 'Hazmat', key: 'hazmatRequired', loadKey: 'hazmatRequired' },
    { label: 'Ramps Required', key: 'ramps', loadKey: 'ramps' },
    { label: 'Tarps Required', key: 'tarpRequired', loadKey: 'tarpRequired' },
    { label: 'Over Dimensions', key: 'dimension', loadKey: 'od' },
  ];

  const { form, data, setFields, errors, addField, unsetField, validate } =
    createForm<PostLoadValues>({
      initialValues: {
        pickUp: '',
        equipment: '',
        origin: '',
        destination: '',
        originCity: '',
        originState: '',
        originCountry: 'USA',
        originZip: '',
        mileage: 0,
        price: null,
        destinationCity: '',
        destinationState: '',
        destinationCountry: 'USA',
        destinationZip: '',
        dropOff: '',
        drops: 0,
        mode: 'TL',
        tags: [],
        weight: 0,
        length: 0,
        id: 0,
        loadId: 0,
        boardsPostedTo: [],
        servicesToPostTo: [],
        internalNote: '',
        comment: '',
      },
      extend: validator({ schema: postLoad }),
      onSubmit: (values) => {
        void handleSubmit(values);
      },
      onError: (errors) => {
        printLog(errors, 'Form Errors');
      },
    });

  const loadEquipmentTypeCodes = async (
    equipment: string,
    tags: string[],
    length: number,
    servicesToPostTo: string[],
  ) => {
    try {
      setIsEquipmentCodesLoading(true);
      const codes = await fetchEquipmentTypeCodes({
        equipment,
        tags,
        length,
        servicesToPostTo,
      });
      const map: Record<string, string> = {};
      codes.forEach((code: EquipmentTypeCodeBoard) => {
        map[code.boardName] = code.effectiveCode;
      });
      setBoardEffectiveCodes(map);
    } catch (error) {
      logError('loadEquipmentTypeCodes', error as Error);
    } finally {
      setIsEquipmentCodesLoading(false);
    }
  };

  const debouncedLoadEquipmentTypeCodes = debounce(loadEquipmentTypeCodes, 400);
  onCleanup(() => debouncedLoadEquipmentTypeCodes.cancel());

  const triggerEquipmentCodesFetch = (overrides?: {
    equipment?: string;
    tags?: string[];
    length?: number;
    servicesToPostTo?: string[];
  }) => {
    const equipment = overrides?.equipment ?? data().equipment;
    const tags = overrides?.tags ?? data().tags;
    const length = Number(overrides?.length ?? data().length);
    const servicesToPostTo =
      overrides?.servicesToPostTo ?? data().servicesToPostTo;

    if (!equipment || servicesToPostTo.length === 0) {
      setBoardEffectiveCodes({});
      return;
    }

    void debouncedLoadEquipmentTypeCodes(equipment, [...tags], length, [
      ...servicesToPostTo,
    ]);
  };

  // Code shown next to a board: the manual override wins over the API value.
  const displayedCode = (board: string) =>
    editedCodes()[board] ?? boardEffectiveCodes()[board];

  const setEditedCode = (board: string, value: string) => {
    setEditedCodes((prev) => ({ ...prev, [board]: value }));
  };

  const subcodeValue = (board: string) => editedSubcodes()[board] ?? '';

  const setEditedSubcode = (board: string, value: string) => {
    setEditedSubcodes((prev) => ({ ...prev, [board]: value }));
  };

  // Pre-edit values captured when a board's pencil is clicked, so Cancel can
  // restore them (onChange writes straight into editedCodes/editedSubcodes).
  const [editSnapshot, setEditSnapshot] = createSignal<{
    code: string | undefined;
    subcode: string | undefined;
  } | null>(null);

  const startEditingCode = (board: string) => {
    setEditSnapshot({
      code: editedCodes()[board],
      subcode: editedSubcodes()[board],
    });
    setEditingBoard(board);
  };

  const restoreBoardValue = (
    setter: typeof setEditedCodes,
    board: string,
    value: string | undefined,
  ) => {
    setter((prev) => {
      const next = { ...prev };
      if (value === undefined) {
        delete next[board];
      } else {
        next[board] = value;
      }
      return next;
    });
  };

  const cancelEditingCode = (board: string) => {
    const snapshot = editSnapshot();
    restoreBoardValue(setEditedCodes, board, snapshot?.code);
    restoreBoardValue(setEditedSubcodes, board, snapshot?.subcode);
    setEditSnapshot(null);
    setEditingBoard(null);
  };

  const stopEditingCode = (board: string) => {
    // An override cleared to empty is dropped so the board falls back to the
    // API code and contributes null to the payload.
    setEditedCodes((prev) => {
      const current = prev[board];
      if (current !== undefined && current.trim() === '') {
        const next = { ...prev };
        delete next[board];
        return next;
      }
      return prev;
    });
    // Likewise drop an emptied subcode so it submits null.
    setEditedSubcodes((prev) => {
      const current = prev[board];
      if (current !== undefined && current.trim() === '') {
        const next = { ...prev };
        delete next[board];
        return next;
      }
      return prev;
    });
    setEditSnapshot(null);
    setEditingBoard(null);
  };

  // eslint-disable-next-line complexity
  createEffect(() => {
    if (
      props.load.stops &&
      props.load.stops.length > 0 &&
      props.postingId === null
    ) {
      const origin = props.load.stops.find((stop) => {
        return (
          stop.stopOrder ===
            Math.min(
              ...(props.load.stops ?? []).map(
                (stop) => stop.stopOrder as number,
              ),
            ) && stop.pickUp === true
        );
      });
      const destination = props.load.stops.find((stop) => {
        return (
          stop.stopOrder ===
            Math.max(
              ...(props.load.stops ?? []).map(
                (stop) => stop.stopOrder as number,
              ),
            ) && stop.dropOff === true
        );
      });

      setFields('origin', getLocationString(origin as LastUsedTopStopModel));
      setFields(
        'destination',
        getLocationString(destination as LastUsedTopStopModel),
      );
      setFields('pickUp', origin?.stopDateTime ?? '');
      setFields('dropOff', destination?.stopDateTime ?? '');
      setFields('originCity', origin?.city ?? '');
      setFields('originState', origin?.state ?? '');
      setFields(
        'originCountry',
        normalizeCountryCode(origin?.countryCode ?? ''),
      );
      setFields('destinationCity', destination?.city ?? '');
      setFields('destinationState', destination?.state ?? '');
      setFields(
        'destinationCountry',
        normalizeCountryCode(destination?.countryCode ?? ''),
      );
      setFields('originZip', origin?.zip ?? '');
      setFields('destinationZip', destination?.zip ?? '');
      setFields(
        'servicesToPostTo',
        orderStore.availableServices
          .filter((service) => service.quickPostInclude)
          .map((service) => service.name),
      );
      setFields('equipment', props.load.equipment as string);
      setFields('mileage', props.load.practicalMileage ?? 0);
      setFields(
        'price',
        isAutoPopulatePostingFromLoadEnabled() &&
          !props.load.postingId &&
          !!props.load.id
          ? props.load.maxPrice
            ? Math.round(props.load.maxPrice)
            : null
          : null,
      );
      setFields(
        'weight',
        props.load.items?.reduce((acc, v) => {
          return v.operationType !== 'Delete' ? acc + v.weight : acc;
        }, 0) ?? 0,
      );
      setFields('length', getLength());
      setFields('id', 0);
      setFields('drops', drops() >= 2 ? drops() - 2 : 0);
      setFields('loadId', props.load.id);
      setFields('boardsPostedTo', []);
      setFields('internalNote', '');
      const loadNumNew = props.load.loadNum ?? '';
      setFields(
        'comment',
        canPrepopulateSingleComment() && loadNumNew ? loadNumNew : '',
      );
      setFields(
        'mode',
        isPostingModeOptionsEnabled()
          ? getPostingModeFromLoadMode(props.load.mode)
          : props.load.mode === 'TL'
            ? 'TL'
            : 'LTL',
      );

      const initialServices = orderStore.availableServices
        .filter((service) => service.quickPostInclude)
        .map((service) => service.name);
      triggerEquipmentCodesFetch({
        equipment: props.load.equipment as string,
        tags: [],
        length: getLength(),
        servicesToPostTo: initialServices,
      });
    } else if (props.postingId !== null) {
      setPosingField();
      triggerEquipmentCodesFetch({
        equipment: orderStore.managePostLoad.equipment,
        tags: orderStore.managePostLoad.tags ?? [],
        length: Number(orderStore.managePostLoad.length),
        servicesToPostTo: orderStore.managePostLoad.boardsPostedTo ?? [],
      });
    }
  });

  const setPosingField = () => {
    const origin = {
      city: orderStore.managePostLoad.originCity,
      state: orderStore.managePostLoad.originState,
      countryCode: orderStore.managePostLoad.originCountry,
    };
    const destination = {
      city: orderStore.managePostLoad.destinationCity,
      state: orderStore.managePostLoad.destinationState,
      countryCode: orderStore.managePostLoad.destinationCountry,
    };
    setFields('origin', getLocationString(origin as LastUsedTopStopModel));
    setFields(
      'destination',
      getLocationString(destination as LastUsedTopStopModel),
    );
    setFields('originCity', orderStore.managePostLoad.originCity);
    setFields('originState', orderStore.managePostLoad.originState);
    setFields(
      'originCountry',
      normalizeCountryCode(orderStore.managePostLoad.originCountry),
    );
    setFields('destinationCity', orderStore.managePostLoad.destinationCity);
    setFields('destinationState', orderStore.managePostLoad.destinationState);
    setFields('originZip', orderStore.managePostLoad.originZip ?? '');
    setFields('destinationZip', orderStore.managePostLoad.destinationZip ?? '');
    setFields(
      'destinationCountry',
      normalizeCountryCode(orderStore.managePostLoad.destinationCountry),
    );

    setFields('equipment', orderStore.managePostLoad.equipment);
    setFields('price', orderStore.managePostLoad.price);
    setFields('weight', orderStore.managePostLoad.weight);
    setFields('length', getLength());
    setFields('id', orderStore.managePostLoad.id);
    setFields('loadId', orderStore.managePostLoad.loadId);
    setFields('boardsPostedTo', orderStore.managePostLoad.boardsPostedTo || []);
    setFields(
      'servicesToPostTo',
      orderStore.managePostLoad.boardsPostedTo || [],
    );
    // Restore previously saved manual overrides so the edited code (not a fresh
    // equipmenttypecode value) is shown and resubmitted when reopening a posting.
    if (isEquipmentCodeOverrideEnabled()) {
      const boardNames = props.services.map((service) => service.name);
      setEditedCodes(
        hydrateEquipmentOverrides(orderStore.managePostLoad, boardNames),
      );
      setEditedSubcodes(
        hydrateEquipmentSubcodeOverrides(orderStore.managePostLoad, boardNames),
      );
    }
    setFields('internalNote', orderStore.managePostLoad.internalNote);
    const existingComment = orderStore.managePostLoad.comment;
    const loadNumEdit =
      (orderStore.managePostLoad.loadNumber as string | undefined) ??
      props.load.loadNum ??
      '';
    const hasLeadingLoadNumMatch =
      !!loadNumEdit &&
      new RegExp(
        `^${loadNumEdit.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(?:\\s|$)`,
      ).test(existingComment);
    setFields(
      'comment',
      canPrepopulateSingleComment() && loadNumEdit && !hasLeadingLoadNumMatch
        ? existingComment
          ? `${loadNumEdit} ${existingComment}`
          : loadNumEdit
        : existingComment,
    );
    setFields('mode', getPostingModeFromPosting(orderStore.managePostLoad));
    setFields('tags', orderStore.managePostLoad.tags ?? []);
    setFields('pickUp', orderStore.managePostLoad.pickUp);
    setFields('dropOff', orderStore.managePostLoad.dropOff);
    setFields('drops', orderStore.managePostLoad.drops);
    setFields('weight', orderStore.managePostLoad.weight);
    setFields('mileage', orderStore.managePostLoad.mileage);
    setFields('length', getLength());
  };

  function getValue<T>(defaultValue: T, storeValue: T): T {
    return props.postingId === null ? defaultValue : storeValue;
  }

  const parseEquipmentFeet = (equipment: unknown): number | null => {
    if (typeof equipment !== 'string') return null;

    // Require at least one digit before apostrophe, e.g. 53'
    const match = equipment.match(/(\d+)\s*'/);
    if (!match) return null;

    const feet = Number(match[1]);
    return Number.isFinite(feet) && feet > 0 ? feet : null;
  };

  const getLength = () => {
    const selectedEquipment = getValue(
      props.load.equipment,
      orderStore.managePostLoad.equipment,
    );

    const equipmentFeet = parseEquipmentFeet(selectedEquipment);
    if (equipmentFeet !== null) return equipmentFeet;

    if (props.postingId !== null) {
      const savedLength = Number(orderStore.managePostLoad.length);
      return Number.isFinite(savedLength) && savedLength > 0 ? savedLength : 48;
    }

    const itemsLength =
      props.load.items?.reduce((acc, v) => {
        if (v.operationType === 'Delete') return acc;

        const itemLength = Number(v.length);
        return Number.isFinite(itemLength) ? acc + itemLength : acc;
      }, 0) ?? 0;

    return itemsLength > 0 ? itemsLength : 48;
  };

  const drops = () => props.load.stops?.length ?? 0;
  const handleSubmit = async (values: PostLoadValues) => {
    values.originCountry = reverseNormalizeCountryCode(values.originCountry);
    values.destinationCountry = reverseNormalizeCountryCode(
      values.destinationCountry,
    );
    updateOrderField('loadsPayments', values);
    setLoading(true);
    const postwithoutBoards = { ...values };
    delete postwithoutBoards.boardsPostedTo;
    if (
      !Array.isArray(postwithoutBoards.servicesToPostTo) ||
      postwithoutBoards.servicesToPostTo.length === 0
    ) {
      handleToast(ToastType.Error, 'Please select at least one board to post');
      setLoading(false);
      return;
    }

    // Overrides only contribute to the payload while the feature flag is on.
    const overrides = isEquipmentCodeOverrideEnabled()
      ? buildEquipmentOverridePayload(
          editedCodes(),
          postwithoutBoards.servicesToPostTo,
          editedSubcodes(),
        )
      : {};

    const modePayload = getPostingModePayload(
      values.mode,
      isPostingModeOptionsEnabled(),
    );

    try {
      if (props.postingId === null) {
        await savePostLoad({
          ...postwithoutBoards,
          ...modePayload,
          ...overrides,
        } as unknown as IPostLoad);
        setLoading(false);
      } else {
        await editPostLoad({
          ...values,
          ...modePayload,
          ...overrides,
        } as unknown as IManagePostLoad);
        setLoading(false);
      }

      closeModal(props.modalId);
    } catch (error) {
      if (!(error instanceof Error)) {
        error = createError(error, 'Error saving load');
      }

      logError(
        props.postingId === null ? 'savePostLoad' : 'editPostLoad',
        error as Error,
      );

      handleToast(ToastType.Error, (error as Error).message);

      printLog('errors', error);
      setLoading(false);
    }
  };

  const OriginDestination = () => {
    const handleLocationSelect = (
      item: ILocationSearch,
      type: 'origin' | 'destination',
    ) => {
      setFields(type, `${item.city}, ${item.state}`);
      setFields(`${type}City`, item.city);
      setFields(`${type}State`, item.state);
      setFields(`${type}Zip`, item.zipcode);
    };

    return (
      <Box class="flex flex-col" gap={2}>
        <Box class="flex gap-2 items-start w-full">
          <Box class="flex-1 min-w-0">
            <Combobox<ILocationSearch>
              label="Origin"
              value={data().origin}
              error={errors().origin?.[0] ?? null}
              fetchData={async (query) => {
                const country =
                  data().originCountry === 'USA' ? 'US' : data().originCountry;
                const res = await apiClient.get(
                  `capacity/autocomplete?country=${country}&term=${encodeURIComponent(query.trimStart())}`,
                );
                const items =
                  'value' in (res as object)
                    ? (res as unknown as ApiResponse<ILocationSearch[]>).value
                    : (res as unknown as ILocationSearch[]);
                return items.slice(0, 50);
              }}
              getOptionLabel={(item) => item.display}
              renderItem={(item) => item.display}
              onSelect={(item) => {
                if (item) handleLocationSelect(item, 'origin');
                else setFields('origin', '');
              }}
            />
          </Box>
          <Box class="w-[100px] pt-[6px]">
            <SelectInput
              menuItems={COUNTRY_CODES.map((c) => ({
                label: c.label,
                value: c.value,
              }))}
              label="Country"
              value={data().originCountry}
              onChange={(newCountry) => {
                setFields((prev) => {
                  if (prev.originCountry !== newCountry) {
                    return {
                      ...prev,
                      originCity: '',
                      originState: '',
                      originZip: '',
                      origin: '',
                      originCountry: newCountry,
                    };
                  }
                  return {
                    ...prev,
                    originCountry: newCountry,
                  };
                });
              }}
              dataTestId={formatDataTestId(
                'Post load origin country',
                'selectInput',
              )}
            />
          </Box>
          <Box class="w-[180px] pt-[6px]">
            <DatePicker
              label=""
              placeholder="Pick"
              value={data().pickUp}
              handleChange={(date) => {
                setFields('pickUp', date);
              }}
              error={errors().pickUp}
              dataTestId="pick-datePicker"
            />
          </Box>
        </Box>
        <Box class="flex gap-2 items-start w-full">
          <Box class="flex-1 min-w-0">
            <Combobox<ILocationSearch>
              label="Destination"
              value={data().destination}
              error={errors().destination?.[0] ?? null}
              fetchData={async (query) => {
                const country =
                  data().destinationCountry === 'USA'
                    ? 'US'
                    : data().destinationCountry;
                const res = await apiClient.get(
                  `capacity/autocomplete?country=${country}&term=${encodeURIComponent(query.trimStart())}`,
                );
                const items =
                  'value' in (res as object)
                    ? (res as unknown as ApiResponse<ILocationSearch[]>).value
                    : (res as unknown as ILocationSearch[]);
                return items.slice(0, 50);
              }}
              getOptionLabel={(item) => item.display}
              renderItem={(item) => item.display}
              onSelect={(item) => {
                if (item) handleLocationSelect(item, 'destination');
                else setFields('destination', '');
              }}
            />
          </Box>
          <Box class="w-[100px] pt-[6px]">
            <SelectInput
              menuItems={COUNTRY_CODES.map((c) => ({
                label: c.label,
                value: c.value,
              }))}
              label="Country"
              value={data().destinationCountry}
              onChange={(newCountry) => {
                setFields((prev) => {
                  if (prev.destinationCountry !== newCountry) {
                    return {
                      ...prev,
                      destinationCity: '',
                      destinationState: '',
                      destinationZip: '',
                      destination: '',
                      destinationCountry: newCountry,
                    };
                  }
                  return {
                    ...prev,
                    destinationCountry: newCountry,
                  };
                });
              }}
              dataTestId={formatDataTestId(
                'Post load destination country',
                'selectInput',
              )}
            />
          </Box>
          <Box class="w-[180px] pt-[6px] flex flex-col">
            <DatePicker
              label=""
              placeholder="Drop"
              value={data().dropOff}
              handleChange={(date) => {
                setFields('dropOff', date);
              }}
              error={errors().dropOff}
              dataTestId="drop-datePicker"
            />
            <Typography
              variant="body1"
              component="span"
              class="text-[#00000099]! text-xs! pl-2!"
            >
              Optional
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  };

  const CommentSection = () => {
    return (
      <Grid container spacing={4} class="mt-2">
        <Grid item size={{ xs: 12, md: 6 }}>
          <TextField
            value={data().comment}
            id="comment"
            multiline={true}
            rows={5}
            maxLength={70}
            label={'Posting Comment'}
            onChange={(value: string | number | Event | null) =>
              setFields('comment', value as string)
            }
            dataTestId="posting-comment-textInput"
          />
          <Typography
            variant="body1"
            component="span"
            class="text-[#00000099]! text-xs! pt-1"
          >
            {70 - (data().comment?.length ?? 0)} chars remaining
          </Typography>
        </Grid>
        <Grid item size={{ xs: 12, md: 6 }}>
          <TextField
            value={data().internalNote}
            id="note"
            multiline={true}
            rows={5}
            label={'Internal Note'}
            onChange={(value: string) => setFields('internalNote', value)}
            dataTestId="internal-note-textInput"
          />
        </Grid>
      </Grid>
    );
  };

  const handleServices = (item: boolean, name: string, index: number) => {
    const current = data().servicesToPostTo;
    const nextServices = item
      ? [...current, name]
      : current.filter((s) => s !== name);
    if (item) {
      addField('servicesToPostTo', name, index);
      // Only a newly checked board needs a code fetch; unchecking just hides
      // the board (other boards' codes are unchanged), so no call is needed.
      // Gated with the rest of the override feature — pre-feature behavior did
      // not refetch codes on board selection.
      if (isEquipmentCodeOverrideEnabled()) {
        triggerEquipmentCodesFetch({ servicesToPostTo: nextServices });
      }
    } else {
      const indexToBeDeleted = current.indexOf(name);
      unsetField(`servicesToPostTo.${indexToBeDeleted}`);
    }
  };

  const handleTags = (item: boolean, name: string, index: number) => {
    const current = data().tags;
    const nextTags = item
      ? [...current, name]
      : current.filter((t) => t !== name);
    if (item) {
      addField('tags', name, index);
    } else {
      const indexToBeDeleted = current.indexOf(name);
      unsetField(`tags.${indexToBeDeleted}`);
    }
    triggerEquipmentCodesFetch({ tags: nextTags });
  };

  const Services = () => {
    const checkForServices = (label: string) => {
      return data().servicesToPostTo.includes(label);
    };
    const slug = (name: string) =>
      name.replace(/\s+/g, '-').replace(/:/g, '').toLowerCase();
    return (
      <div class={`${cls.flexWithCol} min-w-0 w-full`}>
        <div class="flex items-center gap-1">
          <Typography variant="body1" children={'Boards'} />
          <Show when={isEquipmentCodeOverrideEnabled()}>
            <HintTooltip text="Equipment codes are auto-filled based on your selected tags, equipment, and length. Click the pencil icon next to any code to enter a custom override. Overrides persist even if other settings change.">
              <span class="flex items-center">
                <InformationIcon size={18} color="#026EA1" />
              </span>
            </HintTooltip>
          </Show>
        </div>
        <Show when={isEquipmentCodesLoading()}>
          <AtgLoader />
        </Show>

        {props.services.map((val: availableServices, index) => {
          if (props.postingId === null && val.quickPostInclude === true) {
            addField('servicesToPostTo', val.name, index);
          }

          return (
            <div class="flex flex-wrap items-center gap-x-1 w-full">
              <Checkbox
                checked={checkForServices(val.name)}
                label={
                  isEquipmentCodeOverrideEnabled() ? (
                    val.name
                  ) : (
                    <span class="flex flex-wrap items-baseline gap-x-1 break-words leading-tight max-w-full">
                      <span>{val.name}</span>
                      <Show when={boardEffectiveCodes()[val.name]}>
                        <span class="text-[#00000099] text-xs break-all whitespace-normal">
                          ({boardEffectiveCodes()[val.name]})
                        </span>
                      </Show>
                    </span>
                  )
                }
                onChange={(item) => {
                  handleServices(item, val.name, index);
                }}
                dataTestId={`${slug(val.name)}-checkboxInput`}
                size="xs"
                color="info"
                class="ml-[-9px]"
              />
              <Show
                when={
                  isEquipmentCodeOverrideEnabled() &&
                  checkForServices(val.name) &&
                  (displayedCode(val.name) || editingBoard() === val.name)
                }
              >
                <Show
                  when={editingBoard() === val.name}
                  fallback={
                    <>
                      <span class="text-[#00000099] text-xs leading-none break-all whitespace-normal">
                        ({displayedCode(val.name)})
                      </span>
                      <span class="ml-auto flex shrink-0 items-center">
                        <IconButton
                          title={`Edit ${val.name} equipment code`}
                          size="xs"
                          dataTestId={`${slug(val.name)}-code-editButton`}
                          onClick={() => startEditingCode(val.name)}
                        >
                          <PencilIcon size="14px" color="#026EA1" />
                        </IconButton>
                      </span>
                    </>
                  }
                >
                  <span class="flex grow shrink-0 basis-[170px] items-start gap-2">
                    <span class="flex min-w-0 flex-1 flex-col gap-1">
                      <TextField
                        value={displayedCode(val.name) ?? ''}
                        size="xs"
                        class="w-full"
                        onChange={(value) => setEditedCode(val.name, value)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter') {
                            event.preventDefault();
                            stopEditingCode(val.name);
                          } else if (event.key === 'Escape') {
                            event.preventDefault();
                            cancelEditingCode(val.name);
                          }
                        }}
                        dataTestId={`${slug(val.name)}-code-textInput`}
                      />
                      <Show when={boardHasSubcode(val.name)}>
                        <TextField
                          value={subcodeValue(val.name)}
                          size="xs"
                          label="Subcode"
                          class="w-full"
                          onChange={(value) =>
                            setEditedSubcode(val.name, value)
                          }
                          onKeyDown={(event) => {
                            if (event.key === 'Enter') {
                              event.preventDefault();
                              stopEditingCode(val.name);
                            } else if (event.key === 'Escape') {
                              event.preventDefault();
                              cancelEditingCode(val.name);
                            }
                          }}
                          endIcon={
                            <button
                              type="button"
                              title={`Clear ${val.name} subcode`}
                              aria-label={`Clear ${val.name} subcode`}
                              class="flex shrink-0 cursor-pointer items-center border-0 bg-transparent p-0"
                              data-testid={`${slug(val.name)}-subcode-clearButton`}
                              onClick={() => setEditedSubcode(val.name, '')}
                            >
                              <CloseIcon size="16px" color="#00000099" />
                            </button>
                          }
                          dataTestId={`${slug(val.name)}-subcode-textInput`}
                        />
                      </Show>
                    </span>
                    <span class="flex shrink-0 items-center gap-1 ">
                      <IconButton
                        title={`Save ${val.name} equipment code`}
                        size="xs"
                        class="shrink-0"
                        dataTestId={`${slug(val.name)}-code-saveButton`}
                        onClick={() => stopEditingCode(val.name)}
                      >
                        <CheckCircleIcon size="20px" color="#1B873F" />
                      </IconButton>
                      <IconButton
                        title={`Cancel editing ${val.name} equipment code`}
                        size="xs"
                        class="shrink-0"
                        dataTestId={`${slug(val.name)}-code-cancelButton`}
                        onClick={() => cancelEditingCode(val.name)}
                      >
                        <CloseCircleIcon size="20px" color="#C62828" />
                      </IconButton>
                    </span>
                  </span>
                </Show>
              </Show>
            </div>
          );
        })}
      </div>
    );
  };

  const TagsSection = () => {
    const checkForTags = (label: string) => {
      return data().tags.includes(label);
    };
    return (
      <div>
        <Typography variant="body1" children={'Tags'} />
        <div class="flex flex-wrap ml-[15px] gap-2 mt-1">
          {Tags.map((val, index) => {
            return (
              <Checkbox
                checked={checkForTags(val.label)}
                label={val.label}
                onChange={(item) => {
                  handleTags(item, val.label, index);
                }}
                dataTestId={`${val.label.replace(/\s+/g, '-').replace(/:/g, '').toLowerCase()}-checkboxInput`}
                size="xs"
                color="info"
                class="ml-[-9px]"
              />
            );
          })}
        </div>
        <Show
          when={
            // If there's an existing posting and there's a load id.
            isAutoPopulatePostingFromLoadEnabled() &&
            !!props.load.postingId &&
            !!props.load.id
          }
        >
          <Typography variant="body1" children="Load Tags" />
          <div class="flex flex-wrap ml-[15px] gap-2 mt-1">
            {Tags.map((val) => {
              return (
                <Checkbox
                  checked={
                    (props.load[val.loadKey as keyof ILoadEditViewModel] ??
                      false) as boolean
                  }
                  label={val.label}
                  disabled
                  dataTestId={`${val.label.replace(/\s+/g, '-').replace(/:/g, '').toLowerCase()}-checkboxInput`}
                  size="xs"
                  color="info"
                  class="ml-[-9px]"
                />
              );
            })}
          </div>
        </Show>
      </div>
    );
  };

  const handleEquipmentChange = (value: string) => {
    const feet = parseEquipmentFeet(value);
    if (feet !== null) setFields('length', feet);
  };

  return (
    <form ref={form}>
      <Box>
        <Grid container spacing={4}>
          <Grid item size={showPricingDetails() ? 8 : 12}>
            <Box style={{ 'flex-grow': 1 }}>
              <Grid container spacing={4}>
                <Grid
                  item
                  size={{
                    xs: 12,
                    md: isEquipmentCodeOverrideEnabled() ? 7 : 7.5,
                  }}
                >
                  <OriginDestination />
                </Grid>
                <Grid
                  item
                  size={{
                    xs: 12,
                    md: isEquipmentCodeOverrideEnabled() ? 1.5 : 2,
                  }}
                >
                  <Box class="relative top-[6px]">
                    <TextField
                      label="Drops"
                      type="number"
                      value={data().drops}
                      onChange={(item: unknown) =>
                        setFields('drops', Number(item as number))
                      }
                      dataTestId="drops-textInput"
                    />
                  </Box>
                </Grid>
                <Grid
                  item
                  size={{
                    xs: 12,
                    md: isEquipmentCodeOverrideEnabled() ? 3.5 : 2.5,
                  }}
                >
                  <Box class="relative top-[6px]">
                    <Services />
                  </Box>
                </Grid>
              </Grid>
            </Box>
            <Divider class={cls.divider} />
            <Grid container spacing={4} class="mt-1">
              <Grid item size={{ xs: 12, md: 6 }}>
                <Stack spacing={2}>
                  <SelectInput
                    label="Equipment"
                    menuItems={equipmentTypes.map((eq) => ({
                      label: eq.label,
                      value: eq.value,
                    }))}
                    value={data().equipment}
                    onChange={(val) => {
                      handleEquipmentChange(val as string);
                      setFields('equipment', val);
                      triggerEquipmentCodesFetch({ equipment: val as string });
                    }}
                    dataTestId={formatDataTestId(
                      'Post load equipment',
                      'selectInput',
                    )}
                  />
                  <Show
                    when={isPostingModeOptionsEnabled()}
                    fallback={
                      <Checkbox
                        checked={data().mode === 'TL'}
                        label="Full Truck Load?"
                        onChange={(item) =>
                          setFields('mode', item ? 'TL' : 'LTL')
                        }
                        dataTestId="full-truck-load-checkboxInput"
                        size="xs"
                        color="info"
                        class="ml-[-9px]"
                      />
                    }
                  >
                    <div data-testid="post-load-mode-radioGroup">
                      <RadioGroup
                        label="Mode"
                        color="info"
                        value={data().mode}
                        options={POSTING_MODE_OPTIONS}
                        optionClass="[&_input]:p-[3px]!"
                        onChange={(value) => setFields('mode', value)}
                      />
                    </div>
                  </Show>
                  <TextField
                    label="Weight (lbs)"
                    value={data().weight}
                    type="number"
                    onChange={(item: unknown) =>
                      setFields('weight', Number(item as number))
                    }
                    dataTestId="weight-lbs-textInput"
                  />
                  <TextField
                    label="Length (feet)"
                    value={data().length === 0 ? '' : data().length}
                    type="number"
                    onChange={(item: unknown) =>
                      setFields('length', Number(item))
                    }
                    dataTestId="length-feet-textInput"
                  />
                </Stack>
              </Grid>
              <Grid item size={{ xs: 12, md: 6 }}>
                <Stack spacing={2}>
                  <Typography
                    variant="body1"
                    children={
                      <Box>
                        <Show
                          when={!data().mileage}
                          fallback={
                            <span>Mileage: ~{data().mileage} miles</span>
                          }
                        >
                          <span>Enter mileage and destination</span>
                        </Show>
                        <Show
                          when={
                            // If there's an existing posting and there's a load id.
                            isAutoPopulatePostingFromLoadEnabled() &&
                            !!props.load.postingId &&
                            !!props.load.id
                          }
                        >
                          &nbsp;&nbsp;
                          <span>
                            Load Max Price:{' '}
                            {formatAmount(props.load.maxPrice, undefined, '-')}
                          </span>
                        </Show>
                      </Box>
                    }
                    class={cls.fontBold}
                  />
                  <Box display="flex" gap={1}>
                    <TextField
                      id="price"
                      label="Price (USD)"
                      class="flex-1!"
                      value={data().price as number}
                      endIcon={<InformationOutlineIcon color="#026EA1" />}
                      onChange={(value: unknown) =>
                        setFields('price', Number(value as number))
                      }
                      dataTestId="price-usd-textInput"
                    />
                    <Button
                      title="Suggest Pricing"
                      class="w-fit!"
                      label="Suggest Pricing"
                      onClick={async () => {
                        const isValid = await validate();
                        const hasZip =
                          data().originZip && data().destinationZip;
                        if (!hasZip) {
                          handleToast(
                            ToastType.Caution,
                            'Please reassingn origin and destination',
                          );
                          return;
                        }
                        if (
                          isValid &&
                          isValid.origin?.length === 0 &&
                          isValid.destination?.length === 0
                        ) {
                          setShowPricingDetails(false);
                          setShowPricingDetails(true);
                        }
                      }}
                      dataTestId={formatDataTestId('Suggest Pricing', 'button')}
                    />
                  </Box>
                  <TagsSection />
                </Stack>
              </Grid>
            </Grid>
          </Grid>
          <Show when={showPricingDetails()}>
            <Grid item size={{ xs: 12, md: showPricingDetails() ? 4 : 0 }}>
              <PostingPricingDetails
                renderAtPath="PostLoad"
                origination={{
                  address: null,
                  city: data().originCity,
                  state: data().originState,
                  country: data().originCountry || 'USA',
                  zip: data().originZip,
                }}
                destination={{
                  address: null,
                  city: data().destinationCity,
                  state: data().destinationState,
                  country: data().destinationCountry || 'USA',
                  zip: data().destinationZip,
                }}
                equipment={data().equipment}
                setShowPricingDetails={setShowPricingDetails}
              />
            </Grid>
          </Show>
        </Grid>
        <Divider class={cls.divider} />
        <CommentSection />
      </Box>

      <Box display="flex" mt={3} justifyContent="end" alignItems="center">
        {props.postingId !== null && (
          <Button
            label={'Remove Posting'}
            type="submit"
            variant="contained"
            class="mr-auto! bg-[red]!"
            onClick={async () => {
              setDeleteLoading(true);
              try {
                await deletePosting(
                  props.postingId,
                  CHANGE_TRACKING_SOURCES.orderDetails.loadDetailsPostLoad,
                );
                updateLoadPropertyAtIndex({ postingId: null });
                setDeleteLoading(false);
                closeModal(props.modalId);
              } catch (error) {
                handleToast(ToastType.Error, (error as Error).message);
                setDeleteLoading(false);
              }
            }}
            disabled={isDeleteLoading()}
            isLoading={isDeleteLoading()}
            dataTestId={formatDataTestId('Remove Posting', 'button')}
          />
        )}
        <Button
          label={props.postingId === null ? 'Post Load' : 'Edit Posting'}
          type="submit"
          variant="contained"
          disabled={isLoading()}
          isLoading={isLoading()}
          dataTestId={formatDataTestId('Post Load Edit Posting', 'button')}
        />
        {props.postingId === null && (
          <Button
            variant="outlined"
            label="Cancel"
            onClick={() => {
              closeModal(props.modalId);
            }}
            dataTestId={formatDataTestId('Cancel', 'button')}
          />
        )}
      </Box>
    </form>
  );
};
